/**
 * PWA App Mode Detection
 *
 * Detects whether the app is running as an installed PWA (app mode)
 * vs running in a regular browser tab (browser mode)
 */

/**
 * Check if the app is running as an installed PWA
 *
 * Detection methods:
 * 1. iOS: navigator.standalone
 * 2. Android/Desktop: display-mode: standalone media query
 * 3. Fallback: Check if launched from Android app
 *
 * @returns true if running as installed app, false if running in browser
 */
export function isRunningAsApp(): boolean {
  // iOS standalone detection
  if ('standalone' in navigator && (navigator as any).standalone === true) {
    return true;
  }

  // Android/Desktop PWA detection
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }

  // Fallback: Android app referrer
  if (document.referrer.includes('android-app://')) {
    return true;
  }

  return false;
}

/**
 * Check if PWA is likely installed (not definitive)
 *
 * Uses heuristics to guess if user has app installed.
 * Not 100% accurate but useful for showing "Open in app" prompts.
 *
 * @returns true if app is likely installed
 */
export function isAppLikelyInstalled(): boolean {
  // If already in app mode, definitely installed
  if (isRunningAsApp()) {
    return true;
  }

  // Check if we've set a flag indicating previous app mode
  try {
    const hasUsedApp = localStorage.getItem('vybe_app_installed');
    if (hasUsedApp === 'true') {
      return true;
    }
  } catch {
    // localStorage not available
  }

  // No reliable way to detect from browser context
  return false;
}

/**
 * Mark that user has used the app
 * Call this from app mode to help with future detection
 */
export function markAppAsInstalled(): void {
  try {
    localStorage.setItem('vybe_app_installed', 'true');
  } catch {
    // localStorage not available
  }
}

/**
 * Get the app mode for analytics/debugging
 */
export function getAppMode(): 'app' | 'browser' {
  return isRunningAsApp() ? 'app' : 'browser';
}
